import { action, computed, makeAutoObservable, runInAction } from "mobx"
import { rootStore } from "./index";
import {
    AddressSuggestion,
    Coupon,
    LoyaltyProgram,
    NewOrder,
    NewOrderItem,
    NewOrderPaymentDetails,
    OperationType,
    Order,
    PaymentProviderType,
    RecipientType
} from "../services/order/models";
import OrdersRestService, { OrderCreatedRest } from "../services/order/rest";
import {
    Coordinates, DeliveryConditionType,
    DeliveryDetails,
    DeliveryType, PriceInCurrency
} from "../services/menu/models";


class Checkout {
    order: Order | null = null;
    deliveryDetails: DeliveryDetails | null = null;
    paymentDetails: NewOrderPaymentDetails | null = null;
    deliveryType: DeliveryType = DeliveryType.toTable;
    paymentType: PaymentProviderType = PaymentProviderType.offline;
    loyaltyType: OperationType = "Withdraw";
    loyaltyPhone:string = "";
    isReadyToSubmit: boolean = false;
    comment: string = "";
    clientId: number | null = null;
    coupon: Coupon | null = null;
    failedCouponCheck: boolean = false;
    couponCode: string = "";
    isCouponCodeChecking: boolean = false;
    timer: any;
    addressQuery: string = "";
    isAddressQueryChecking: boolean = false;
    failedAddressCheck: boolean = false;
    addressSuggestions: AddressSuggestion[] = [] ;
    isBonusCardModalOpen: boolean = false;
    loyaltyProgram: LoyaltyProgram | null = null;
    validateTakeAwayToTime: boolean = true;
    zoneFixedPrice: PriceInCurrency = {
                                            currencyId: "RUB",
                                            price: 0
                                        };
    zoneFreeDeliveryMinimalOrderPrice: PriceInCurrency = {
                                                            currencyId: "RUB",
                                                            price: 0
                                                        };
    isZoneEnabledForDelivery: boolean = true;


    constructor() {
        makeAutoObservable(this);
        this.init();
    }

    async getDeliveryPriceByCoordinates (coordinates: Coordinates) {
        const res = await OrdersRestService.getDeliveryPrice(
            rootStore.generalStore.menuId,
            coordinates.latitude,
            coordinates.longitude,
        );

        if (!res) return;

        runInAction(() => {
            this.isZoneEnabledForDelivery = res.isAddressInDeliveryZone;

            if (res.fixedDeliveryPrice && res.freeDeliveryMinimalOrderPrice) {
                this.zoneFixedPrice = res.fixedDeliveryPrice;
                this.zoneFreeDeliveryMinimalOrderPrice = res.freeDeliveryMinimalOrderPrice;
            }
        })

    }

    @action
    async init() {

    }

    isCouponValid(coupon: Coupon | null){
        if(coupon && new Date(coupon.data.startDate).getTime() < Date.now() && new Date(coupon.data.endDate).getTime() > Date.now()){
            return true
        }else{
            return false
        }
    }

    @action
    setIsReadyToSubmit(value: boolean) {
        this.isReadyToSubmit = value;
    }


    @action
    setData(obj: any) {
        this.deliveryDetails = obj.deliveryDetails;
        this.deliveryType = obj.deliveryType;
        this.comment = obj.comment || "";
    }

    @action
    setPaymentType(type: PaymentProviderType) {
        this.paymentType = type;
    }

    @action
    setDeliveryType(type: DeliveryType) {
        this.deliveryType = type;
    }

    @action
    setLoyaltyType(type: OperationType) {
        this.loyaltyType = type;
    }
    @action
    setLoyaltyPhone(phone: string) {
        this.loyaltyPhone = phone;
    }


    async createOrder(): Promise<{ err: false, order: Order } | { err: true, msg: string }> {
        const store = rootStore;
        if (!this.deliveryDetails) {
            return {
                err: true, msg: "Не указаны детали доставки"
            }
        }
        if(!this.paymentDetails) {
            return {
                err: true, msg: "Нет деталей оплаты"
            }
        }

        let items: NewOrderItem[] = [];
        store.cartStore.list.forEach(itemsByMenuId => {
            itemsByMenuId.items.forEach(item => {
                items.push({
                    chosenMenuItemModifierSets: item.chosenMenuItemModifierSets,
                    comment: null,
                    menuItemId: item.menuItem.id,
                    menuItemsCount: item.count
                })
            })
        });


        const order: NewOrder = {
            clientSessionId: store.generalStore.clientSessionId,
            items,
            menuId: store.generalStore.menuId,
            deliveryDetails: this.deliveryDetails,
            clientLocaleId: store.generalStore.locale.id,
            paymentDetails: this.paymentDetails,
            comment: this.comment,
            couponCode: this.coupon?.data.code || null,
            loyaltyProgram: this.loyaltyProgram,
            orderSessionId: store.generalStore.orderSessionId
        };

        let createdOrder: OrderCreatedRest | null;

        try {
            createdOrder = await OrdersRestService.createOrder(order);
        } catch (e) {
            return {
                err: true,
                msg: `Failed to create order`
            }
        }

        if (!createdOrder) {
            return {
                err: true,
                msg: `Failed to create order`
            }
        }

        const getOrderResponse = await OrdersRestService.getOrder(createdOrder.orderId);

        if (!getOrderResponse) {
            return {
                err: true,
                msg: `Failed to get order by id ${createdOrder.orderId}`
            }
        }

        runInAction(() => {
            this.order = getOrderResponse.order;
            this.clientId = createdOrder!.clientId;
        });

        this.saveOrderDataForFutureUsage(order);

        return {
            err: false,
            order: getOrderResponse.order
        }
    }

    @action
    reset() {
        this.order = null;
        this.deliveryDetails = null;
        this.deliveryType = DeliveryType.toTable;
        this.comment = '';
        this.coupon = null;
        this.couponCode = '';
    }


    saveOrderDataForFutureUsage(order: NewOrder){
        if(order.deliveryDetails.type === DeliveryType.toAddress){
            rootStore.generalStore.saveAddress(order.deliveryDetails.address);
            rootStore.generalStore.savePhone(order.deliveryDetails.phoneNumber);

        }else if(order.deliveryDetails.type === DeliveryType.takeaway ||
            order.deliveryDetails.type === DeliveryType.takeawayToTime){
            rootStore.generalStore.savePhone(order.deliveryDetails.phoneNumber);
        }
    }

    async setCouponCode(code: string){

        runInAction(() => {
            this.couponCode = code;
        })
        if(this.timer){
            clearTimeout(this.timer);
            this.timer = undefined;
        }

        if(code.length === 0){
            this.setFailedCouponCheck(false);
            this.setIsCouponLoading(false);
            return;
        }

        const now = Date.now();
        this.setIsCouponLoading(true);

        const generalStore = rootStore.generalStore;

        // Ненайденный и исчерпанный промокод бэк разводит по статусам, но для корзины это
        // одно и то же: применить нельзя.
        const coupon = await OrdersRestService.getCoupon(code, generalStore.menuId);

        if(Date.now() - now < 1000){
            this.timer = setTimeout(() => {
                this.setFailedCouponCheck(coupon === null)
                this.setCoupon(coupon);
                this.setIsCouponLoading(false);
            }, 1000)
        }else{
            this.setFailedCouponCheck(coupon === null)
            this.setCoupon(coupon);
            this.setIsCouponLoading(false);
        }
    }

    @action
    setFailedCouponCheck(v: boolean) {
        this.failedCouponCheck = v;
    }
    @action
    setIsCouponLoading(v: boolean) {
        this.isCouponCodeChecking = v;
    }

    @action
    setCoupon(coupon: Coupon | null){
        this.coupon = coupon;
    }

    @action
    resetCouponCode(){
        this.couponCode = ""
    }

    async setDeliveryAddress(address: string) {

        runInAction(() => {
            this.addressQuery = address;
        })
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = undefined;
        }

        if (address.length === 0) {
            this.setFailedAddressCheck(false);
            this.setIsAddressQueryChecking(false);
            return;
        }

        const now = Date.now();
        this.setIsAddressQueryChecking(true);

        const generalStore = rootStore.generalStore;
        const suggestions = await OrdersRestService.getAddressSuggestions(generalStore.menuId, address) ?? [];

        if (Date.now() - now < 1000) {
            this.timer = setTimeout(() => {
                this.setFailedAddressCheck(suggestions.length === 0)
                this.setAddressSuggestions(suggestions);
                this.setIsAddressQueryChecking(false);
            }, 1000)
        } else {
            this.setFailedAddressCheck(suggestions.length === 0)
            this.setAddressSuggestions(suggestions);
            this.setIsAddressQueryChecking(false);
        }
    }

    @action
    setIsAddressQueryChecking(v: boolean) {
        this.isAddressQueryChecking = v;
    }

    @action
    setFailedAddressCheck(v: boolean) {
        this.failedAddressCheck = v;
    }

    @action
    setAddressSuggestions(addressSuggestions: AddressSuggestion[]) {
        this.addressSuggestions = addressSuggestions;
    }


    async reloadOrder() {
        if (!this.order) return;

        const getOrderResponse = await OrdersRestService.getOrder(this.order.id);

        if (!getOrderResponse) {
            return {
                err: true,
                msg: `Failed to get order by id ${this.order.id}`
            }
        }
        runInAction(() => {
            this.order = getOrderResponse.order;
        });
    }

    static fromJSON(obj: any) {
        const checkout = new Checkout();
        checkout.setData(obj);
        return checkout;
    }

    @action
    setDeliveryDetails(deliveryDetails: DeliveryDetails) {
        this.deliveryDetails = deliveryDetails;
    }
    @action
    setLoyaltyProgram(loyaltyProgram: LoyaltyProgram) {
        this.loyaltyProgram = loyaltyProgram;
    }

    @action
    setPaymentDetails(paymentDetails: NewOrderPaymentDetails) {
        this.paymentDetails = paymentDetails;
    }

    @action
    setComment(comment: string) {
        this.comment = comment;
    }


    @computed
    validateInvoiceToCompanyDetails() {
        if (this.paymentDetails === null) {
            return false;
        }

        if (this.paymentDetails.type !== PaymentProviderType.companyInvoice) return true;
        if (this.paymentDetails.recipientCompanyCard.contractId.length === 0) {
            return false;
        }

        if (this.paymentDetails.recipientCompanyCard.shortTitle.length < 10) {
            return false;
        }

        if (this.paymentDetails.recipientCompanyCard.legalAddress.length < 10) {
            return false;
        }

        if (this.paymentDetails.recipientCompanyCard.type === RecipientType.russianOOOCard) {
            if (this.paymentDetails.recipientCompanyCard.inn.length !== 10) {
                return false;
            }

            if (this.paymentDetails.recipientCompanyCard.kpp.length !== 9) {
                return false;
            }
        } else if (this.paymentDetails.recipientCompanyCard.type === RecipientType.russianIPCard) {
            if (this.paymentDetails.recipientCompanyCard.inn.length !== 12) {
                return false;
            }

            if (this.paymentDetails.recipientCompanyCard.ogrnip.length !== 15) {
                return false;
            }
        }

        return true;
    }

    @computed
    validatePaymentDetails() {
        if (this.paymentDetails === null) return false;

        if ([PaymentProviderType.offline,
            PaymentProviderType.toBankCard,
            PaymentProviderType.cloudpayments]
            .indexOf(this.paymentDetails.type) >= 0) {
            return true
        } else {
            return this.validateInvoiceToCompanyDetails();
        }
    }

    @computed
    validateDeliveryTakeAwayToTimeDetails(v: boolean) {
        this.validateTakeAwayToTime = v;
    }

    @computed
    validateDeliveryDetails() {
        if (this.deliveryDetails?.type === DeliveryType.toTable) {
            const table = rootStore.menuStore.getTableById(this.deliveryDetails.tableId);
            let hasTableBeenChosen = false;
            let hasSeats = false;
            if (table) {
                hasTableBeenChosen = true;
                hasSeats = table.data.seats.length > 0;
            }
            const hasToChooseSeat = rootStore.menuStore.menu.data.deliverySettings[DeliveryType.toTable].canChooseSeat && hasSeats;
            return (hasToChooseSeat
                && this.deliveryDetails.seatId !== null
                || !hasToChooseSeat)
                && hasTableBeenChosen && !!table && table.data.publicId !== 0;
        } else if (this.deliveryDetails?.type === DeliveryType.toAddress){

            const dom8OrgBranch = 699

            if (rootStore.menuStore.menu.organizationBranchId == dom8OrgBranch) {
                const dom8DataFilled = (this.deliveryDetails.name?.length || 0) > 0 && (this.deliveryDetails.porch?.length || 0) > 0 && this.deliveryDetails.flat != null && this.deliveryDetails.floor != null

                if (rootStore.menuStore.menu.data.deliverySettings[DeliveryType.toAddress]?.deliveryConditions.type === DeliveryConditionType.zonePrice) {
                    return this.deliveryDetails.phoneNumber.length > 0 && this.deliveryDetails.address.length > 5 && this.isZoneEnabledForDelivery && dom8DataFilled;
                } else return this.deliveryDetails.phoneNumber.length > 0 && this.deliveryDetails.address.length > 5 && dom8DataFilled;
            } else {
                if (rootStore.menuStore.menu.data.deliverySettings[DeliveryType.toAddress]?.deliveryConditions.type === DeliveryConditionType.zonePrice) {
                    return this.deliveryDetails.phoneNumber.length > 0 && this.deliveryDetails.address.length > 5 && this.isZoneEnabledForDelivery;
                } else return this.deliveryDetails.phoneNumber.length > 0 && this.deliveryDetails.address.length > 5;
            }
        }else if(this.deliveryDetails?.type === DeliveryType.takeaway ){
            return this.deliveryDetails.phoneNumber.length > 9
        }else if(this.deliveryDetails?.type === DeliveryType.takeawayToTime){
            return (this.deliveryDetails.phoneNumber.length > 9 && this.validateTakeAwayToTime)
        } else if(this.deliveryDetails?.type === DeliveryType.toVehicle){
            const phoneCorrect = this.deliveryDetails.phoneNumber.length > 9;
            const hasPlateNumber = this.deliveryDetails.vehiclePlateNumber.length >= 3;
            return phoneCorrect && hasPlateNumber;
        }else if(this.deliveryDetails?.type === DeliveryType.insideTheRestaurant){
            return true;
        }
        return false;
    }

    @action
    openBonusCardModal (onOverlayClick: () => void) {
        this.isBonusCardModalOpen = true;
        rootStore.generalMenuStore.isOverlayOpen = true;
        rootStore.generalMenuStore.onOverlayClick = onOverlayClick;
        document.body.style.overflow = 'hidden';
    }

    @action
    closeBonusCardModal() {
        this.isBonusCardModalOpen = false;
        rootStore.generalMenuStore.isOverlayOpen = false;
        rootStore.generalMenuStore.onOverlayClick = undefined;
        document.body.style.overflow = 'auto';
    }
}

export default Checkout
